import '@app/lib/yup'
import { DateInput, type ModalProps, SelectInput, TextInput, UnsavedChangesModal } from '@app/components'
import { showMessagesNotifications, useCreateEnrollment } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { Alert, Button, Grid, Group, Stack } from '@mantine/core'
import { useDidUpdate } from '@mantine/hooks'
import { IconCheck } from '@tabler/icons-react'
import { useForm, yupResolver } from '@mantine/form'
import { type FC, useCallback, useState } from 'react'
import * as yup from 'yup'
import dayjs from 'dayjs'

type FormData = {
  startDate: Date | null
  studentDob: Date | null
  studentFirstName: string
  studentLastName: string
  parentEmail: string
  parentFirstName: string
  parentLastName: string
  parentPhone: string
  participationType: string
}

export interface EnrollmentAddModalProps extends Omit<ModalProps, 'children' | 'title'> {}

export const EnrollmentAddModal: FC<EnrollmentAddModalProps> = ({ onClose, ...props }) => {
  const { options, rosterProgramYearId: programYearId, siteId } = useSugarContext()
  const { isCreatingEnrollment, createEnrollment } = useCreateEnrollment()
  const [changed, setChanged] = useState(false)
  const form = useForm<FormData>({
    initialValues: {
      studentFirstName: '',
      studentLastName: '',
      studentDob: null,
      startDate: null,
      parentEmail: '',
      parentFirstName: '',
      parentLastName: '',
      parentPhone: '',
      participationType: ''
    },
    validate: yupResolver(
      yup.object({
        parentEmail: yup.string().trim().email('Invalid parent email!').required('Parent email is required!'),
        parentFirstName: yup.string().trim().required('Parent first name is required!'),
        parentLastName: yup.string().trim().required('Parent first name is required!'),
        parentPhone: yup.string().trim().phone('Invalid parent phone!').required('Parent first name is required!'),
        startDate: yup.date().required('Start date is required!'),
        studentFirstName: yup.string().trim().required('Student first name is required!'),
        studentLastName: yup.string().trim().required('Student last name is required!'),
        studentDob: yup.date().required('Student date of birth type is required!'),
        participationType: yup.string().trim().required('Participation type is required!')
      })
    ),
    validateInputOnChange: true
  })
  const isDirty = form.isDirty()
  const isValid = form.isValid()

  const handleClose = useCallback(() => {
    form.reset()
    onClose()
  }, [form, onClose])
  const handleSubmit = useCallback(async () => {
    if (siteId && programYearId) {
      const { startDate, studentDob, ...data } = form.values
      const created = await createEnrollment({
        ...data,
        programYearId,
        siteId,
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        studentDob: dayjs(studentDob).format('YYYY-MM-DD')
      })

      showMessagesNotifications(created.messages)

      if (created.success) {
        handleClose()
      }
    }
  }, [siteId, programYearId, form.values, createEnrollment, handleClose])

  useDidUpdate(() => {
    setChanged(isDirty)
  }, [isDirty])

  return (
    <UnsavedChangesModal
      closeOnClickOutside
      closeOnEscape
      isChanged={changed}
      isLoading={isCreatingEnrollment}
      onClose={handleClose}
      size='xl'
      title='Add Student'
      {...props}
    >
      <Stack spacing='xl'>
        <Alert
          color='green'
          radius='lg'
        >
          Complete the following fields for each student you believe to be eligible. We must have the student name,
          participation type, DOB and start date to be able to review the family&#39;s eligibility. Once you have
          completed the information, click the &#34;Save&#34; button to your request. You may click the &#34;Close&#34;
          button to if you no longer wish to add another student.
        </Alert>
        <Grid gutter='xl'>
          <Grid.Col span={6}>
            <TextInput
              disabled={isCreatingEnrollment}
              label='Student First Name'
              required
              tabIndex={2}
              {...form.getInputProps('studentFirstName')}
            />
            <DateInput
              disabled={isCreatingEnrollment}
              label='Student Date of Birth'
              required
              tabIndex={4}
              {...form.getInputProps('studentDob')}
            />
            <SelectInput
              data={options?.participationTypes ?? []}
              disabled={isCreatingEnrollment}
              label='Participation Type'
              required
              tabIndex={6}
              {...form.getInputProps('participationType')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              disabled={isCreatingEnrollment}
              label='Student Last Name'
              required
              tabIndex={3}
              {...form.getInputProps('studentLastName')}
            />
            <DateInput
              disabled={isCreatingEnrollment}
              label='Start Date'
              required
              tabIndex={5}
              {...form.getInputProps('startDate')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              disabled={isCreatingEnrollment}
              label='Parent/Guardian First Name'
              required
              tabIndex={7}
              {...form.getInputProps('parentFirstName')}
            />
            <TextInput
              disabled={isCreatingEnrollment}
              label='Parent/Guardian Email'
              required
              tabIndex={9}
              {...form.getInputProps('parentEmail')}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              disabled={isCreatingEnrollment}
              label='Parent/Guardian Last Name'
              required
              tabIndex={8}
              {...form.getInputProps('parentLastName')}
            />
            <TextInput
              disabled={isCreatingEnrollment}
              label='Parent/Guardian Phone'
              required
              tabIndex={10}
              {...form.getInputProps('parentPhone')}
            />
          </Grid.Col>
        </Grid>
        <Group position='right'>
          <Button
            disabled={isCreatingEnrollment || !isValid}
            leftIcon={<IconCheck size={18} />}
            onClick={handleSubmit}
            tabIndex={6}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </UnsavedChangesModal>
  )
}
